import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import APQRCode from '@alphapoint/apex-web/lib/components/common/APQRCode/APQRCode';
import APIcon from '@alphapoint/apex-web/lib/components/common/APIcon';
import Spinner from '@alphapoint/apex-web/lib/components/common/Spinner/Spinner';
import { getBEMClasses } from '@alphapoint/apex-web/lib/helpers/cssClassesHelper';
import clipboardHelper from '@alphapoint/apex-web/lib/helpers/clipboardHelper';
import destinationTagHelper from '@alphapoint/apex-web/lib/helpers/destinationTagHelper';
import { InnerSelect } from '@alphapoint/apex-web/lib/components/common/APSelect';
import '@alphapoint/apex-web/lib/components/SendReceiveSidePane/ReceiveAddress/ReceiveAddress.css';

const bemClasses = getBEMClasses('receive-address');

// Override apex-web's .receive-address__address max-width: 35rem + overflow-x: auto
// which causes a horizontal scrollbar for full crypto addresses (42+ chars).
const addressStyle = {
  maxWidth: 'none',
  overflow: 'visible',
  wordBreak: 'break-all',
};

const navBtnStyle = {
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  fontSize: 22,
  padding: '0 12px',
  lineHeight: 1,
  color: 'inherit',
};

const navBtnDisabledStyle = {
  ...navBtnStyle,
  cursor: 'default',
  opacity: 0.3,
};

const navRowStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: '8px 0',
};

const counterStyle = {
  minWidth: 56,
  textAlign: 'center',
  fontVariantNumeric: 'tabular-nums',
};

const generateBtnStyle = {
  display: 'block',
  margin: '12px auto 0',
  padding: '8px 16px',
  border: '1px solid currentColor',
  background: 'transparent',
  color: 'inherit',
  cursor: 'pointer',
  borderRadius: 3,
  font: 'inherit',
};

class ReceiveAddressComponent extends Component {
  state = {
    selectedAccountProviderId: null,
    currentIndex: null,
  };

  componentDidMount() {
    const { selectDepositProduct, product } = this.props;
    selectDepositProduct(product.ProductId);
  }

  componentDidUpdate(prevProps) {
    const prevList = (prevProps.deposit && prevProps.deposit.depositInfo) || [];
    const nextList = (this.props.deposit && this.props.deposit.depositInfo) || [];
    if (nextList.length && nextList.length !== prevList.length) {
      // initial load (0 -> N) lands on the first; Generate (N -> N+1) lands on the new last.
      const targetIndex = prevList.length === 0 ? 0 : nextList.length - 1;
      this.setState({ currentIndex: targetIndex });
    }
  }

  handleSelectAccountProvider = (accountProviderId) => {
    const { fetchAccountProviderDepositInfo, product } = this.props;
    if (!accountProviderId) {
      this.setState({ selectedAccountProviderId: accountProviderId, currentIndex: null });
      return;
    }
    fetchAccountProviderDepositInfo(product.ProductId, accountProviderId);
    this.setState({ selectedAccountProviderId: accountProviderId, currentIndex: null });
  };

  handlePrev = () => {
    this.setState((s) => ({ currentIndex: Math.max(0, (s.currentIndex ?? 0) - 1) }));
  };

  handleNext = () => {
    const total = (this.props.deposit.depositInfo || []).length;
    this.setState((s) => ({ currentIndex: Math.min(total - 1, (s.currentIndex ?? 0) + 1) }));
  };

  handleGenerate = () => {
    const { generateNewDepositAddress, product } = this.props;
    const { selectedAccountProviderId } = this.state;
    if (!selectedAccountProviderId) return;
    generateNewDepositAddress(product.ProductId, selectedAccountProviderId);
  };

  render() {
    const { t, deposit, product, showSnack, WalletsDisabledMessage } = this.props;
    const { selectedAccountProviderId, currentIndex } = this.state;
    const { isLoading, error, templateTypes } = deposit;
    const addressList = deposit.depositInfo || [];

    const depositType = product.ProductFullName.toLowerCase().replace(/\W/g, '');

    const templateTypeOptions = [
      { value: '', label: 'Select an option', disabled: true, selected: true },
      ...templateTypes.map((template) => ({
        value: template.AccountProviderId,
        label: template.AccountProviderName,
      })),
    ];

    const total = addressList.length;
    const safeIndex = total
      ? Math.min(Math.max(currentIndex ?? 0, 0), total - 1)
      : null;
    const selectedAddress = !isLoading && safeIndex !== null ? addressList[safeIndex] : undefined;
    const { address, dt, memo } = destinationTagHelper(selectedAddress);
    const atFirst = total <= 1 || safeIndex === 0;
    const atLast = total <= 1 || safeIndex === total - 1;

    return (
      <section className={bemClasses()}>
        {templateTypes.length ? (
          <InnerSelect
            name="AccountProvider"
            customClass={bemClasses('account-provider-select')}
            label={t('Select an option to continue the deposit process')}
            onSelect={this.handleSelectAccountProvider}
            options={templateTypeOptions}
          />
        ) : (
          <div>{t('No account providers are configured')}</div>
        )}

        {selectedAddress && selectedAccountProviderId && (
          <Fragment>
            <p className={bemClasses('description')}>{t('Scan this QR code to receive funds:')}</p>
            <div className={bemClasses('qr-code')}>
              <APQRCode value={`${depositType}:${selectedAddress}`} />
            </div>

            <p className={bemClasses('copy-address-text')}>{t('Or copy this address:')}</p>
            <div
              className={bemClasses('copy-address-payload')}
              onClick={() => {
                clipboardHelper(address);
                showSnack({
                  id: 'RECEIVE_ADDRESS_COPY',
                  text: t('The address has been copied to the clipboard.'),
                });
              }}
            >
              <span className={bemClasses('address')} style={addressStyle}>{address}</span>
              <APIcon name="copy" customClass={bemClasses('copy-icon')} />
            </div>

            {dt && (
              <div
                onClick={() => {
                  clipboardHelper(dt);
                  showSnack({
                    id: 'RECEIVE_ADDRESS_COPY',
                    text: t('The destination tag has been copied to the clipboard.'),
                  });
                }}
              >
                <p className={bemClasses('destination-tag-text')}>
                  {t('Be sure to include this destination tag:')}
                </p>
                <span className={bemClasses('address')} style={addressStyle}>{dt}</span>
                <APIcon name="copy" customClass={bemClasses('copy-icon')} />
              </div>
            )}

            {memo && (
              <div
                onClick={() => {
                  clipboardHelper(memo);
                  showSnack({
                    id: 'RECEIVE_ADDRESS_COPY',
                    text: t('The memo tag has been copied to the clipboard.'),
                  });
                }}
              >
                <p className={bemClasses('memo-tag-text')}>{t('Be sure to include this memo tag:')}</p>
                <span className={bemClasses('address')} style={addressStyle}>{memo}</span>
                <APIcon name="copy" customClass={bemClasses('copy-icon')} />
              </div>
            )}

            {total > 1 && (
              <div style={navRowStyle}>
                <button
                  type="button"
                  onClick={this.handlePrev}
                  disabled={atFirst}
                  style={atFirst ? navBtnDisabledStyle : navBtnStyle}
                  aria-label={t('Previous address')}
                >
                  ‹
                </button>
                <span style={counterStyle}>{`${safeIndex + 1} / ${total}`}</span>
                <button
                  type="button"
                  onClick={this.handleNext}
                  disabled={atLast}
                  style={atLast ? navBtnDisabledStyle : navBtnStyle}
                  aria-label={t('Next address')}
                >
                  ›
                </button>
              </div>
            )}

            <button
              type="button"
              onClick={this.handleGenerate}
              disabled={isLoading}
              style={generateBtnStyle}
            >
              {t('Generate new address')}
            </button>
          </Fragment>
        )}

        {isLoading && <Spinner text={t('Loading url...')} customClass={bemClasses} />}

        {WalletsDisabledMessage ? (
          <span className={bemClasses('error')}>{t(WalletsDisabledMessage)}</span>
        ) : (
          error && <span className={bemClasses('error')}>{t(error)}</span>
        )}
      </section>
    );
  }
}

ReceiveAddressComponent.defaultProps = {
  deposit: {
    templateInfo: {},
    templateTypes: [],
    template: {},
    isLoading: false,
    error: '',
    depositInfo: [],
    depositStatus: {},
  },
  product: {},
};

ReceiveAddressComponent.propTypes = {
  product: PropTypes.object,
  deposit: PropTypes.shape({
    templateInfo: PropTypes.object,
    templateTypes: PropTypes.array,
    template: PropTypes.object,
    isLoading: PropTypes.bool,
    error: PropTypes.string,
    product: PropTypes.number,
    depositInfo: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    depositStatus: PropTypes.shape({
      success: PropTypes.bool,
      RequestCode: PropTypes.string,
    }),
  }),
  selectDepositProduct: PropTypes.func.isRequired,
  fetchAccountProviderDepositInfo: PropTypes.func.isRequired,
  generateNewDepositAddress: PropTypes.func.isRequired,
  showSnack: PropTypes.func.isRequired,
  WalletsDisabledMessage: PropTypes.string,
};

export default withTranslation('send')(ReceiveAddressComponent);
